import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import reportWebVitals from './components/.shared/reportWebVitals';
import { SourceRedirect } from './utils/SourceRedirect';
import { SourceProvider } from './utils/sourceContext';
import { UserEngagementProvider } from './utils/userEngagementContext';
import { InterviewProvider } from './utils/interviewContext';
import { PostHogTracker } from './components/PostHogTracker/PostHogTracker';
import { TidioPosthogBridge } from './utils/tidioPosthogBridge';
import { ConsentGuard } from './utils/ConsentGuard';

import './index.css';
import 'animate.css';
import Privacy from './components/Privacy/Privacy';
import Terms from './components/Terms/Terms';
import CookiePolicyPage from './components/CookiePolicy/CookiePolicy';
import SuccessStoriesPage from './components/SuccessStories/SuccessStories';
import Contact from './components/ContactUs/Contact';
import Welcome from './components/Welcome/Welcome';
import CheckIn from './components/CheckIn/CheckIn';
import Intake from './components/Intake/Intake';
import ArticlesPage from './components/Articles/Articles';
import Landing from './components/Landing/Landing';
import SafeAdsLanding from './components/Landing/SafeAdsLanding';
import KnowledgeCenter from './components/KnowledgeCenter/KnowledgeCenter';
import AboutUs from './components/AboutUs/AboutUs';
import Login from './components/Login/Login';
import Patients from './components/Patients/Patients';
import CheckoutExperimentEntry from './components/CheckoutExperimentEntry/CheckoutExperimentEntry';
import PaymentSuccess from './components/PaymentSuccess/PaymentSuccess';
import Billing from './components/Billing/Billing';
import Decision from './components/Interview/Decision';
import InterviewDiet from './components/Interview/InterviewDiet';
import InterviewMovement from './components/Interview/InterviewMovement';
import InterviewGlp1History from './components/Interview/InterviewGlp1History';
import InterviewHeightWeight from './components/Interview/InterviewHeightWeight';
import InterviewBestYou from './components/Interview/InterviewBestYou';
import InterviewPriority from './components/Interview/InterviewPriority';
import Programs from './components/Programs/Programs';
import Result from './components/Result/Result';
import CEOLanding from './components/CustomLandings/CEOLanding/CEOLanding';
import AffiliateLanding from './components/AffiliateProgram/AffiliateLanding';
import GLP1VettedVendorLanding from './components/CustomLandings/GLP1VettedVendorLanding/GLP1VettedVendorLanding';
import BHVettedVendorLanding from './components/CustomLandings/BHVettedVendorLanding/BHVettedVendorLanding';
import YourJourneyBegins from './components/CustomLandings/YourJourneyBegins/YourJourneyBegins';
import SemaglutideLanding from './components/CustomLandings/SemaglutideLanding/SemaglutideLanding';
import NadLanding from './components/CustomLandings/NadLanding/NadLanding';
import BaseLandingV2 from './components/CustomLandings/BaseLandingV2/BaseLandingV2';
import BenefitHubExperimentEntry from './components/BenefitHubExperimentEntry/BenefitHubExperimentEntry';
import BaseLandingV3 from './components/CustomLandings/BaseLandingV3/BaseLandingV3';
import BaseLandingV4 from './components/CustomLandings/BaseLandingV4/BaseLandingV4';
import BaseLandingV5 from './components/CustomLandings/BaseLandingV5/BaseLandingV5';

const App = () => {
  return (
    <InterviewProvider>
      <UserEngagementProvider>
          <BrowserRouter>
            <SourceProvider>
              <SourceRedirect />
              <PostHogTracker />
              <TidioPosthogBridge />
              <ConsentGuard>
                <Routes>
                  <Route path="login" element={<Login />} />
                  <Route path="patients" element={<Patients />} />
                  <Route path="privacy" element={<Privacy />} />
                  <Route path="terms" element={<Terms />} />
                  <Route path="packages" element={<Programs />} />
                  <Route path="products" element={<Programs />} />
                  <Route path="programs" element={<Programs />} />
                  <Route path="articles" element={<ArticlesPage />} />
                  <Route path="success-stories" element={<SuccessStoriesPage />} />
                  <Route path="contact" element={<Contact />} />
                  <Route path="welcome" element={<Welcome />} />
                  <Route path="checkout" element={<Navigate to="/checkout/payment" replace />} />
                  <Route path="checkout/payment" element={<CheckoutExperimentEntry />} />
                  <Route path="checkout/contact" element={<Navigate to="/checkout/payment" replace />} />
                  <Route path="payment-success" element={<PaymentSuccess />} />
                  <Route path="intake" element={<Intake />} />
                  <Route path="checkin" element={<CheckIn />} />
                  <Route path="start" element={<Programs />} />
                  <Route path="interview/" element={<Decision />} />
                  <Route path="interview/height-weight" element={<InterviewHeightWeight />} />
                  <Route path="interview/bestyou" element={<InterviewBestYou />} />
                  <Route path="interview/priority" element={<InterviewPriority />} />
                  <Route path="interview/diet" element={<InterviewDiet />} />
                  <Route path="interview/movement" element={<InterviewMovement />} />
                  <Route path="interview/glp1-history" element={<InterviewGlp1History />} />
                  <Route path="result" element={<Result />} />
                  <Route path="research" element={<KnowledgeCenter />} />
                  <Route path="safety" element={<KnowledgeCenter />} />
                  <Route path="faq" element={<KnowledgeCenter />} />
                  <Route path="research-center" element={<KnowledgeCenter />} />
                  <Route path="safety-center" element={<KnowledgeCenter />} />
                  <Route path="knowledge-center" element={<KnowledgeCenter />} />
                  <Route path="about" element={<AboutUs />} />
                  <Route path="about-us" element={<AboutUs />} />
                  <Route path="billing" element={<Billing />} />
                  <Route path="landing" element={<Landing />} />
                  <Route path="glp1-vetted-vendor" element={<GLP1VettedVendorLanding />} />
                  <Route path="benefithub" element={<BenefitHubExperimentEntry />} />
                  <Route path="benefithub-vetted-vendor" element={<BHVettedVendorLanding />} />
                  <Route path="ceo_struggle" element={<CEOLanding />} />
                  <Route path="semaglutide" element={<SemaglutideLanding />} />
                  <Route path="nad" element={<NadLanding />} />
                  <Route path="base-landing-v2" element={<BaseLandingV2 />} />
                  <Route path="base-landing-v3" element={<BaseLandingV3 />} />
                  <Route path="base-landing-v4" element={<BaseLandingV4 />} />
                  <Route path="base-landing-v5" element={<BaseLandingV5 />} />
                  <Route path="your-journey-begins" element={<YourJourneyBegins />} />
                  <Route path="affiliates" element={<AffiliateLanding />} />
                  <Route path="cookie-policy" element={<CookiePolicyPage />} />
                  <Route path="medical-weight-loss" element={<SafeAdsLanding />} />
                  <Route path="online-evaluation" element={<SafeAdsLanding />} />
                  <Route path="treatment-pricing" element={<SafeAdsLanding />} />
                  <Route path="*" element={<Landing />} />
                </Routes>
              </ConsentGuard>
            </SourceProvider>
          </BrowserRouter>
      </UserEngagementProvider>
    </InterviewProvider>
  );
};

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(<App />);

reportWebVitals(console.log);
